/**
 * Seeded PRNG utilities.
 *
 * The engine is deterministic: mulberry32 uses stored state for resolution
 * and seed/turn/table/event keys for arrivals, independent of player decisions.
 * Nothing in the engine may call Math.random().
 */

/** Hash an arbitrary string seed into a 32-bit unsigned integer (FNV-1a). */
export function hashSeed(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * A tiny mutable PRNG wrapper. Construct from a state, draw numbers, then read
 * `.state` back into SessionState so the next command continues the sequence.
 */
export class Rng {
  state: number;

  constructor(state: number) {
    this.state = state >>> 0;
  }

  /** Uniform float in [0, 1). mulberry32. */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Integer in [0, n). */
  int(n: number): number {
    if (n <= 0) return 0;
    return Math.floor(this.next() * n);
  }

  /** Pick one element; undefined for an empty array. */
  pick<T>(arr: readonly T[]): T | undefined {
    if (arr.length === 0) return undefined;
    return arr[this.int(arr.length)];
  }

  /** Fisher–Yates shuffle, returning a new array. */
  shuffle<T>(arr: readonly T[]): T[] {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = this.int(i + 1);
      const tmp = a[i] as T;
      a[i] = a[j] as T;
      a[j] = tmp;
    }
    return a;
  }

  /** Choose k distinct indices from [0, n). */
  sampleIndices(n: number, k: number): number[] {
    return this.shuffle(Array.from({ length: n }, (_, i) => i)).slice(0, Math.max(0, Math.min(n, k)));
  }
}

/**
 * Largest-remainder apportionment: split `total` across buckets in proportion
 * to `weights`, returning integers that sum exactly to `total`. Ties broken by
 * bucket order (deterministic).
 */
export function apportion(total: number, weights: readonly number[]): number[] {
  const sum = weights.reduce((a, b) => a + b, 0);
  if (sum <= 0 || total <= 0) return weights.map(() => 0);
  const exact = weights.map((w) => (w / sum) * total);
  const base = exact.map((x) => Math.floor(x));
  let left = total - base.reduce((a, b) => a + b, 0);
  const order = exact
    .map((x, i) => ({ i, r: x - Math.floor(x) }))
    .sort((a, b) => b.r - a.r || a.i - b.i);
  for (const { i } of order) {
    if (left <= 0) break;
    base[i] = (base[i] ?? 0) + 1;
    left--;
  }
  return base;
}

/** Round half up, the convention used for every "exact share" in deck build. */
export function roundShare(n: number, share: number): number {
  return Math.floor(n * share + 0.5);
}
